import { cn, Input, Notice, Select, Textarea } from '@rental/ui'
import { type ComponentProps, Fragment, type ReactNode, useEffect, useRef } from 'react'
import { useLocale } from '../i18n'
import { problemText, type StaffProblem } from '../problems'

/**
 * Two fields side by side only where both fit (DESIGN.md, "A screen and its
 * parts"): a row that wraps, and each field of it 256 px or more, growing to
 * fill the row and shrinking alone on a phone.
 */
export const FIELD_ROW = 'flex flex-wrap gap-4'
export const FIELD = 'min-w-0 grow basis-3xs'
/**
 * A short field, whose words never grow and fit in 128 px: a Z's number, a
 * code of a few letters. Not a date or a time (UI-2g, measured in Chromium): a
 * date shows whole from 152 px beside its calendar and a time from 138, so a
 * lone one is a `FIELD`, and two that go together are a `FieldPair`.
 */
export const SHORT_FIELD = 'min-w-0 grow basis-32'

/**
 * Two fields that go together (a day and its hour, the two days of a period, a
 * birth date and its nationality): one unit in their row, with the room of a
 * field of 320 px, and side by side only where each shows its whole value,
 * from 320 px of the unit's own width (`@xs`): 152 px each, as a date needs.
 * Below that, one under the other (UI-2g). It stands in a field row, never in
 * a column, where a basis would be a height. Its container is sized by its row,
 * never by its fields; marked `data-field-pair`, so the E2E counts a field of a
 * pair on screen with no box as cut: a container query once left its fields
 * with none (DECISIONS, UI-1c part 3).
 */
export function FieldPair({ children }: { children: ReactNode }) {
  return (
    <div className="@container min-w-0 grow basis-xs" data-field-pair>
      <div className={FIELD_ROW}>{children}</div>
    </div>
  )
}

/** A field of a `FieldPair`: the pair's whole width below 320 px, half of it from there. */
export const PAIRED_FIELD = 'min-w-0 grow basis-full @xs:basis-32'

/**
 * An e-mail address that breaks where an address breaks: after its @ and
 * before a dot, never inside a name (UI-2g, the browser QA). In half a pane a
 * fact's column is narrower than an address, and `wrap-anywhere` alone cut it
 * mid-word ("ada.qadesktop@exampl" over "e.org"); it stays the last resort for a
 * name longer than its column.
 */
export function EmailText({ address }: { address: string }) {
  const parts: string[] = []
  let part = ''
  for (const char of address) {
    if (char === '.' && part !== '') {
      parts.push(part)
      part = ''
    }
    part += char
    if (char === '@') {
      parts.push(part)
      part = ''
    }
  }
  if (part !== '') parts.push(part)
  return (
    <>
      {parts.map((piece, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the pieces of one address, in its order
        <Fragment key={index}>
          {index > 0 ? <wbr /> : null}
          {piece}
        </Fragment>
      ))}
    </>
  )
}

/** DESIGN.md: staff inputs have 8 px corners; the customer pill is not for the counter. */
export function StaffInput({ className, ...props }: ComponentProps<typeof Input>) {
  return <Input className={cn('rounded-sm', className)} {...props} />
}

export function StaffSelect({ className, ...props }: ComponentProps<typeof Select>) {
  return <Select className={cn('rounded-sm', className)} {...props} />
}

export function StaffTextarea({ className, ...props }: ComponentProps<typeof Textarea>) {
  return <Textarea className={cn('rounded-sm', className)} {...props} />
}

/**
 * The page's h1, focused on arrival: a screen reader announces where the
 * person is, and the keyboard starts from the top of the new screen.
 */
export function PageHeading({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    ref.current?.focus()
  }, [])
  return (
    // A word wider than the line breaks rather than push the page sideways: a customer's
    // surname is the title, and a long one did at a phone's width (UI-2f, screens.spec).
    <h1
      ref={ref}
      tabIndex={-1}
      className={cn('text-2xl wrap-anywhere focus:outline-none', className)}
    >
      {children}
    </h1>
  )
}

/**
 * A problem from the API, in words, announced at once. `quiet` for one
 * already said where it was asked (in a question just closed): it stays in
 * sight beside the form, and is not said a second time.
 */
export function ProblemNotice({
  problem,
  quiet = false,
}: {
  problem: StaffProblem | null
  quiet?: boolean
}) {
  const { locale, messages } = useLocale()
  if (!problem) return null
  return (
    <Notice tone="danger" {...(quiet ? {} : { live: 'assertive' as const })}>
      <p>{problemText(problem, locale, messages)}</p>
    </Notice>
  )
}

/**
 * Whose a control is, said after its name and not shown (UI-2g): where a
 * screen repeats a form, as Cassa does a drawer's for each pick-up point,
 * each form, field and button ends its name with its own, so no two share one.
 */
export function NamedFor({ name }: { name: string }) {
  return <span className="sr-only">, {name}</span>
}

/** Label and value pairs: a definition list that reflows to two columns. */
export function Facts({ items }: { items: readonly (readonly [ReactNode, ReactNode])[] }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      {items.map(([label, value], index) => (
        // The pairs never move: their position is their identity. A long word
        // (an e-mail address) wraps rather than leaving its pane.
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed list
        <div key={index} className="flex min-w-0 flex-col">
          <dt className="text-sm text-text-muted">{label}</dt>
          <dd className="text-base text-text wrap-anywhere">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * A table wider than the screen (an iPad held upright, a narrow window)
 * scrolls sideways inside a region the keyboard can reach and scroll, named
 * for screen readers (WCAG 2.1.1; axe: scrollable-region-focusable). The
 * theme's focus ring shows where it is. Its name must differ from the
 * section around it: two regions of one name are one landmark too many.
 *
 * Positioned (`relative`), so it holds what is positioned inside it too: a
 * row's hidden words (`sr-only`, absolute) otherwise belong to a box outside
 * the region, which then cannot clip them, and at a phone's width a row
 * button's hidden name widened the page by 246 px (UI-2f, Cassa's Correggi).
 */
export function ScrollRegion({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noNoninteractiveTabindex: a region that scrolls takes focus to be scrolled without a mouse
    <section aria-label={label} tabIndex={0} className="relative overflow-x-auto">
      {children}
    </section>
  )
}

export function Loading() {
  const { messages } = useLocale()
  return (
    <p role="status" className="py-6 text-text-muted">
      {messages.app.loading}
    </p>
  )
}
